/* =========================================================
  CBH debug-safe logging (reduce console noise in production)
  - Shows errors in Theme Editor or when window.CBH_DEBUG = true
========================================================= */
window.CBH_DEBUG = window.CBH_DEBUG || false;

window.cbhLogError = function (...args) {
  try {
    const inEditor = document.documentElement && document.documentElement.classList.contains('shopify-design-mode');
    if (window.CBH_DEBUG || inEditor) {
      console.error(...args);
    }
  } catch (e) {}
};

/* =========================================================
  CBH failsafe: ensure debounce/throttle exist even if other files expect them early
========================================================= */
if (typeof window.debounce !== 'function') {
  window.debounce = function (fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}

if (typeof window.throttle !== 'function') {
  window.throttle = function (fn, delay) {
    let lastCall = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCall < delay) return;
      lastCall = now;
      return fn.apply(this, args);
    };
  };
}

/* Local aliases (avoid duplicate function declarations later) */
const debounce = window.debounce;
const throttle = window.throttle;

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

// CBH: guaranteed design-mode hook (reliable in Theme Editor)
if (window.Shopify && Shopify.designMode) {
  document.documentElement.classList.add('cbh-design-mode');
}

/* =========================================================
  CBD HUT — Scroll lock watchdog (prevents stuck no-scroll)
========================================================= */
function CBH_cleanupScrollLockIfNoSurfacesOpen(force = false) {
  const body = document.body;
  if (!body) return;

  // If any modal is open, do NOT unlock (modals legitimately lock scroll)
  const anyModalOpen = !!document.querySelector('modal-dialog[open], .media-modal[open]');
  if (!force && anyModalOpen) return;

  // If any drawer-like details is open, do NOT unlock
  const anyDetailsOpen = !!document.querySelector(
    'details[open].menu-drawer-container, details.menu-drawer-container[open], details.mobile-facets__disclosure[open], details.mobile-facets__details[open]'
  );
  if (!force && anyDetailsOpen) return;

  // Remove overflow hidden classes Shopify uses
  body.className = body.className.replace(/\boverflow-hidden-[^\s]+\b/g, '').replace(/\boverflow-hidden\b/g, '').trim();

  // If our iOS fixed lock is still applied, fully reset it
  if (body.style.position === 'fixed' || body.style.top) {
    try {
      window.CBH_SCROLL_LOCK?.reset?.();
    } catch (e) {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
    }
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));
    if (!oldNode || !oldNode.parentNode) return;

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    // ✅ After dynamic swaps (facets apply), ensure scroll lock isn't stuck
    // Slight delay reduces race with close animations
    setTimeout(() => CBH_cleanupScrollLockIfNoSurfacesOpen(false), 80);

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling?.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  // IMPORTANT:
  // Drawer components manage aria-expanded + open/close themselves.
  // If we also bind click here, you can get “open then instantly closes” / desync issues.
  if (!summary.closest('header-drawer, menu-drawer')) {
    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }
});

const trapFocusHandlers = {
  focusin: null,
  focusout: null,
  keydown: null,
};

function trapFocus(container, elementToFocus = container) {
  if (!container) return;

  const elements = getFocusableElements(container);
  if (!elements.length) return;

  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    // On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus && elementToFocus.focus) {
    elementToFocus.focus();

    if (
      elementToFocus.tagName === 'INPUT' &&
      ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', () => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement?.classList?.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}
// Ensure availability if any code uses window.pauseAllMedia explicitly
window.pauseAllMedia = window.pauseAllMedia || pauseAllMedia;

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus && elementToFocus.focus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement?.setAttribute?.('aria-expanded', false);
  summaryElement?.focus?.();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input?.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  initHoldToRepeat_() {
    if (this._cbhHoldInit) return;
    this._cbhHoldInit = true;

    const HOLD_DELAY = 650; // ms before repeating starts
    const HOLD_RATE = 260; // ms between repeats (slower)

    const input = this.querySelector('.quantity__input');
    if (!input) return;

    const stepVal = () => parseFloat(input.step || '1') || 1;
    const minVal = () => (input.min !== '' ? parseFloat(input.min) : null);
    const maxVal = () => (input.max !== '' ? parseFloat(input.max) : null);

    const stepOnce = (dir) => {
      let val = parseFloat(input.value || '0') || 0;
      const s = stepVal();
      val = dir === 'plus' ? val + s : val - s;

      const min = minVal();
      const max = maxVal();
      if (min !== null) val = Math.max(min, val);
      if (max !== null) val = Math.min(max, val);

      input.value = String(val);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    // Use AbortController so we can fully clean up on disconnectedCallback (prevents duplicate listeners).
    const ac = window.AbortController ? new AbortController() : null;
    this._cbhHoldAbort = ac;

    const add = (el, type, handler, opts = {}) => {
      const o = { ...opts };
      if (ac) o.signal = ac.signal;
      el.addEventListener(type, handler, o);
    };

    this.querySelectorAll('.quantity__button').forEach((btn) => {
      if (btn.dataset.cbhHoldBound === '1') return;
      btn.dataset.cbhHoldBound = '1';

      const dir = btn.name === 'plus' ? 'plus' : 'minus';

      let delayTimer = null;
      let repeatTimer = null;
      let didHold = false;

      const stop = () => {
        if (delayTimer) window.clearTimeout(delayTimer);
        if (repeatTimer) window.clearInterval(repeatTimer);
        delayTimer = repeatTimer = null;
        didHold = false;
      };

      // Only cancel click IF we actually held long enough to start repeating
      add(
        btn,
        'click',
        (e) => {
          if (btn.dataset.cbhDidHold === '1') {
            e.preventDefault();
            e.stopImmediatePropagation();
            btn.dataset.cbhDidHold = '0';
          }
        },
        true
      );

      add(btn, 'pointerdown', (e) => {
        if (btn.disabled) return;
        if (e?.button !== undefined && e.button !== 0) return;

        btn.setPointerCapture?.(e.pointerId);

        // IMPORTANT: do NOT step here.
        // A normal click will be handled by the theme (adds 1).
        delayTimer = window.setTimeout(() => {
          didHold = true;
          btn.dataset.cbhDidHold = '1';

          // Start repeating only after delay
          stepOnce(dir); // first repeat step at hold start
          repeatTimer = window.setInterval(() => stepOnce(dir), HOLD_RATE);
        }, HOLD_DELAY);
      });

      ['pointerup', 'pointercancel', 'pointerleave', 'lostpointercapture'].forEach((evt) => {
        add(btn, evt, () => {
          if (!didHold) btn.dataset.cbhDidHold = '0';
          stop();
        });
      });
    });

    // teardown hook (called from disconnectedCallback)
    this._cbhHoldStop = () => {
      try {
        ac?.abort?.();
      } catch (e) {}
    };
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    this.initHoldToRepeat_();
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    this._cbhHoldStop?.();
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.input) return;

    const previousValue = this.input.value;

    // IMPORTANT: use currentTarget (button), not target (could be SVG/icon inside button)
    const button = event.currentTarget;
    if (!button || !button.name) return;

    if (button.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && button.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    if (!this.input) return;

    const value = Number.parseInt(this.input.value, 10);
    const val = Number.isFinite(value) ? value : 0;

    if (this.input.min !== '') {
      const min = Number.parseInt(this.input.min, 10);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus && Number.isFinite(min)) {
        buttonMinus.classList.toggle('disabled', val <= min);
      }
    }

    if (this.input.max !== '') {
      const max = Number.parseInt(this.input.max, 10);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus && Number.isFinite(max)) {
        buttonPlus.classList.toggle('disabled', val >= max);
      }
    }
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setPostLink = Shopify.setPostLink || Shopify.postLink;
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function () {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

/* =========================================================
  CBD HUT — Drawer scroll lock (safe)
  - Keeps Shopify’s overflow-hidden-{breakpoint} classes
  - Adds iOS-safe body position:fixed lock on <=989px
  - Ref-counted so multiple locks don’t fight
  - ✅ Resize-safe: never leaves fixed lock stuck across breakpoints
========================================================= */
window.CBH_SCROLL_LOCK = (() => {
  let count = 0;
  let scrollY = 0;
  let fixedApplied = false;

  const isMobileLock = () => window.matchMedia('(max-width: 989px)').matches;

  const applyFixedLock = () => {
    if (fixedApplied) return;
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    fixedApplied = true;
  };

  const removeFixedLock = () => {
    if (!fixedApplied) return;
    const y = scrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    scrollY = 0;
    fixedApplied = false;
    window.scrollTo(0, y);
  };

  return {
    lock(breakpointClass) {
      if (breakpointClass) document.body.classList.add(breakpointClass);

      count += 1;
      if (isMobileLock() && count === 1) applyFixedLock();
    },
    unlock(breakpointClass) {
      if (breakpointClass) document.body.classList.remove(breakpointClass);

      count = Math.max(0, count - 1);
      if (count === 0) removeFixedLock();
    },
    reset() {
      count = 0;
      removeFixedLock();
    },
  };
})();

const CBH_SCROLL_LOCK = window.CBH_SCROLL_LOCK;

/* =========================================================
  CBD HUT — Global pointer tracker (prevents false focusout closes)
========================================================= */
(function () {
  if (window.__CBH_LAST_POINTERDOWN_TRACKER__) return;
  window.__CBH_LAST_POINTERDOWN_TRACKER__ = true;

  window.__CBH_LAST_POINTERDOWN__ = { target: null, path: null, ts: 0 };

  document.addEventListener(
    'pointerdown',
    (e) => {
      window.__CBH_LAST_POINTERDOWN__ = {
        target: e.target || null,
        path: typeof e.composedPath === 'function' ? e.composedPath() : null,
        ts: Date.now(),
      };
    },
    true
  );
})();

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this._cbhScrollClass = null;
    this._cbhSubmenuScroll = new WeakMap();

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();

    // ✅ Safety: ensure we never start with a stale hidden state
    this.resetDrawerState();
  }

  hydrateDrawer() {
    const template = this.querySelector('template[data-cbh-menu-drawer-template]');
    if (!template) return false;

    const summary = this.mainDetailsToggle?.querySelector(':scope > summary') || this.mainDetailsToggle?.querySelector('summary');
    const controlsId = summary?.dataset?.cbhMenuControls;
    const fragment = template.content.cloneNode(true);
    template.replaceWith(fragment);

    if (summary && controlsId) summary.setAttribute('aria-controls', controlsId);

    this.bindEvents();
    this.resetDrawerState();
    return true;
  }

  bindEvents() {
    // Bind summary clicks ONCE (prevents double toggle -> instant close)
    this.querySelectorAll('summary').forEach((summary) => {
      if (summary.dataset.cbhSummaryBound === '1') return;
      summary.dataset.cbhSummaryBound = '1';
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });

    // ✅ Tighten close binding: only real close controls
    this.querySelectorAll('.menu-drawer__close-button, [data-drawer-close]').forEach((el) => {
      if (el.dataset.cbhCloseBound === '1') return;
      el.dataset.cbhCloseBound = '1';
      el.addEventListener('click', this.onCloseButtonClick.bind(this));
    });
  }

  /**
   * ✅ Critical: clears any stale submenu state that can hide the main list
   * (e.g. leftover `.submenu-open`, nested `details[open]`, menu-opening class)
   */
  resetDrawerState() {
    if (!this.mainDetailsToggle) return;

    // Remove "submenu-open" from any ULs (this is what can hide the list)
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((el) => el.classList.remove('submenu-open'));

    // Close ANY nested details (submenus) that might be left open/half-closed
    this.mainDetailsToggle.querySelectorAll('details').forEach((d) => {
      if (d === this.mainDetailsToggle) return;

      d.removeAttribute('open');
      d.classList.remove('menu-opening');
      delete d.dataset.cbhClosing;

      const s = d.querySelector(':scope > summary') || d.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    });

    // Ensure root state isn't flagged as "submenu active"
    this.setSubmenuActive(false);
    this.syncSubmenuActive();
  }

  // Returns open <details> stack (deepest last). Includes the main drawer <details>.
  getOpenDetailsStack() {
    if (!this.mainDetailsToggle) return [];
    const stack = [];

    if (this.mainDetailsToggle.hasAttribute('open')) stack.push(this.mainDetailsToggle);

    // ONLY menu submenus (ignore localisation/details-disclosure etc.)
    stack.push(...Array.from(this.mainDetailsToggle.querySelectorAll('details[open][data-level]')));

    return stack;
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const stack = this.getOpenDetailsStack();
    if (!stack.length) return;

    const deepest = stack[stack.length - 1];

    if (deepest === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    } else {
      this.closeSubmenu(deepest);
    }
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.closest('details');
    if (!detailsElement) return;

    const parentMenuElement = detailsElement.closest('ul.menu-drawer__menu');
    const isMain = detailsElement === this.mainDetailsToggle;

    // MAIN drawer toggle
    if (isMain) {
      const isOpen = detailsElement.hasAttribute('open');
      if (!isOpen) this.hydrateDrawer();
      if (isOpen) event.preventDefault();

      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      // Keep viewport-height variable in sync on mobile/tablet
      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
      return;
    }

    // ✅ If submenu is already open and user taps its summary again, close via our API
    if (detailsElement.hasAttribute('open')) {
      event.preventDefault();
      this.closeSubmenu(detailsElement);
      return;
    }

    // SUBMENUS (opening)
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const panel = summaryElement.nextElementSibling;

    // Save parent scroll (Dawn-like)
    const parentScroller =
      detailsElement.closest('.menu-drawer__inner-submenu') || detailsElement.closest('.menu-drawer__navigation');

    if (parentScroller) {
      this._cbhSubmenuScroll.set(detailsElement, parentScroller.scrollTop || 0);
    }

    const submenuScroller = panel && panel.querySelector('.menu-drawer__inner-submenu');

    const addTrapFocus = () => {
      try {
        const scope = panel || detailsElement;
        const firstFocusable = getFocusableElements(scope)[0] || scope;
        trapFocus(scope, firstFocusable);
      } catch (e) {}
    };

    // ✅ IMPORTANT: set opening state immediately (so CSS can reveal panel once <details> opens)
    detailsElement.classList.add('menu-opening');
    summaryElement.setAttribute('aria-expanded', 'true');
    if (parentMenuElement) parentMenuElement.classList.add('submenu-open');

    requestAnimationFrame(() => {
      this.syncSubmenuActive();

      // Ensure the submenu opens from the top
      if (parentScroller) parentScroller.scrollTop = 0;
      if (submenuScroller) submenuScroller.scrollTop = 0;

      if (reducedMotion) addTrapFocus();
      else requestAnimationFrame(addTrapFocus);
    });
  }

  openMenuDrawer(summaryElement) {
    if (!this.mainDetailsToggle) return;

    // ✅ Reset submenu state when opening the main drawer
    this.setSubmenuActive(false);
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((ul) => ul.classList.remove('submenu-open'));

    // ✅ Clear any stale hidden state before showing the drawer
    this.resetDrawerState();

    // Dawn-like "opening" class for animations/CSS hooks
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', 'true');

    // Trap focus inside panel
    const panel = this.mainDetailsToggle.querySelector('[data-menu-drawer], .menu-drawer') || this.mainDetailsToggle;
    const firstFocusable = getFocusableElements(panel)[0] || summaryElement;
    trapFocus(panel, firstFocusable);

    // Scroll lock
    this._cbhScrollClass = `overflow-hidden-${this.dataset.breakpoint}`;
    CBH_SCROLL_LOCK.lock(this._cbhScrollClass);
  }

  closeMenuDrawer(event = null, elementToFocus = false) {
    if (!this.mainDetailsToggle) return;

    this.setSubmenuActive(false);
    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      delete details.dataset.cbhClosing;
      const s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    });

    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });

    // Unlock scroll
    CBH_SCROLL_LOCK.unlock(this._cbhScrollClass || `overflow-hidden-${this.dataset.breakpoint}`);
    this._cbhScrollClass = null;

    const focusEl = elementToFocus || this.mainDetailsToggle.querySelector('summary');
    if (focusEl) focusEl.setAttribute('aria-expanded', 'false');

    removeTrapFocus(focusEl || null);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.mainDetailsToggle?.hasAttribute('open')) return;

      // If the last tap was inside the drawer, DO NOT close.
      const last = window.__CBH_LAST_POINTERDOWN__;
      if (last && Date.now() - last.ts < 900) {
        const inside =
          (last.path && (last.path.includes(this) || last.path.includes(this.mainDetailsToggle))) ||
          (last.target && this.mainDetailsToggle.contains(last.target));

        if (inside) return;
      }

      // Keyboard tabbing out should still close it.
      if (!this.mainDetailsToggle.contains(document.activeElement)) {
        this.closeMenuDrawer();
      }
    }, 0);
  }

  onCloseButtonClick(event) {
    try {
      event.preventDefault();
      event.stopPropagation();
    } catch (e) {}

    const control = event.currentTarget;
    if (control?.matches?.('[data-drawer-close]')) {
      this.closeMenuDrawer(event, this.mainDetailsToggle?.querySelector('summary'));
      return;
    }

    const stack = this.getOpenDetailsStack();
    if (!stack.length) return;

    const deepest = stack[stack.length - 1];

    if (deepest === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    } else {
      this.closeSubmenu(deepest);
    }
  }

  setSubmenuActive(isActive) {
    this.classList.toggle('cbh-submenu-active', !!isActive);
  }

  syncSubmenuActive(ignoreDetails = null) {
    if (!this.mainDetailsToggle) return;

    // ONLY menu submenus (ignore localisation/details-disclosure etc.)
    const openNested = Array.from(this.mainDetailsToggle.querySelectorAll('details[open][data-level]')).some(
      (d) => d !== ignoreDetails && d?.dataset?.cbhClosing !== '1'
    );

    this.setSubmenuActive(openNested);
  }

  closeSubmenu(detailsElement) {
    if (!detailsElement) return;

    detailsElement.dataset.cbhClosing = '1';
    this.syncSubmenuActive(detailsElement);

    detailsElement.querySelectorAll('details[open]').forEach((d) => {
      this._cbhSubmenuScroll.delete(d);
      d.removeAttribute('open');
      d.classList.remove('menu-opening');
      delete d.dataset.cbhClosing;
      const s = d.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    });

    detailsElement.querySelectorAll('.submenu-open').forEach((el) => el.classList.remove('submenu-open'));

    const owningItem = detailsElement.closest('ul.menu-drawer__menu');
    if (owningItem) owningItem.classList.remove('submenu-open');

    detailsElement.classList.remove('menu-opening');

    const savedScrollTop = this._cbhSubmenuScroll.get(detailsElement);
    const restoreScroller =
      detailsElement.closest('.menu-drawer__inner-submenu') || detailsElement.closest('.menu-drawer__navigation');

    if (restoreScroller && typeof savedScrollTop === 'number') {
      requestAnimationFrame(() => {
        restoreScroller.scrollTop = savedScrollTop;
      });
      this._cbhSubmenuScroll.delete(detailsElement);
    }

    const summary = detailsElement.querySelector(':scope > summary') || detailsElement.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');

    removeTrapFocus(summary || null);

    this.closeAnimation(detailsElement);

    const parentOpen = detailsElement.parentElement?.closest('details[open]');
    const parentSummary = parentOpen ? parentOpen.querySelector(':scope > summary') : null;

    if (parentOpen && parentSummary) {
      window.setTimeout(() => {
        try {
          const parentPanel = parentSummary.nextElementSibling || parentOpen;
          const firstFocusable = getFocusableElements(parentPanel)[0] || parentSummary;
          trapFocus(parentPanel, firstFocusable);
        } catch (e) {}
      }, 420);
    }
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) animationStart = time;

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        delete detailsElement.dataset.cbhClosing;
        this.syncSubmenuActive();
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer', MenuDrawer);
}

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    // ✅ Clear stale submenu-open / hidden states before opening
    this.resetDrawerState();

    // Always reset submenu state when opening the main drawer
    this.setSubmenuActive(false);
    this.mainDetailsToggle?.querySelectorAll?.('.submenu-open')?.forEach((ul) => ul.classList.remove('submenu-open'));

    this.header = this.header || this.closest('.section-header') || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || (this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0);

    // Avoid getBoundingClientRect() (forced reflow). Header is top-aligned so
    // bottom position is effectively its height.
    const bottom = parseInt((this.header.offsetHeight || 0) - this.borderOffset, 10);
    document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
    document.documentElement.style.setProperty('--cbh-header-bottom', `${bottom}px`);

    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', 'true');
    window.addEventListener('resize', this.onResize);

    const panel = this.mainDetailsToggle.querySelector('[data-menu-drawer], .menu-drawer') || this.mainDetailsToggle;
    const firstFocusable = getFocusableElements(panel)[0] || summaryElement;
    trapFocus(panel, firstFocusable);

    this._cbhScrollClass = `overflow-hidden-${this.dataset.breakpoint}`;
    CBH_SCROLL_LOCK.lock(this._cbhScrollClass);
  }

  closeMenuDrawer(event = null, elementToFocus) {
    const focusEl = elementToFocus || this.mainDetailsToggle.querySelector('summary');
    super.closeMenuDrawer(event, focusEl);

    this.header?.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    if (!this.header) return;

    const bottom = parseInt((this.header.offsetHeight || 0) - this.borderOffset, 10);
    document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
    document.documentElement.style.setProperty('--cbh-header-bottom', `${bottom}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    const closeBtn = this.querySelector('[id^="ModalClose-"]');
    closeBtn?.addEventListener('click', this.hide.bind(this, false));

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

connectedCallback() {
  if (this.moved) return;
  this.moved = true;

  const sectionEl = this.closest('.shopify-section');
  if (sectionEl && sectionEl.id) {
    this.dataset.section = sectionEl.id.replace('shopify-section-', '');
  }

  document.body.appendChild(this);
}

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

if (!customElements.get('modal-dialog')) {
  customElements.define('modal-dialog', ModalDialog);
}

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}

if (!customElements.get('modal-opener')) {
  customElements.define('modal-opener', ModalOpener);
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    // CBH: defaults so downstream components never crash when slider markup is absent
    this.sliderItemsToShow = [];
    this.sliderItemOffset = 0;
    this.slidesPerPage = 1;
    this.totalPages = 1;
    this.currentPage = 1;

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this._scheduleInitPages();
    const resizeObserver = new ResizeObserver(() => this._scheduleInitPages());
    resizeObserver.observe(this.slider);

    this._onScroll = this._onScroll || (() => this._scheduleUpdate());
    this.slider.addEventListener('scroll', this._onScroll, { passive: true });
    if (this.prevButton) this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    if (this.nextButton) this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

_scheduleInitPages() {
  if (this._rafInitPages) cancelAnimationFrame(this._rafInitPages);
  this._rafInitPages = requestAnimationFrame(() => {
    this._rafInitPages = null;
    this.initPages();
  });
}

_scheduleUpdate() {
  if (this._rafUpdate) return;
  this._rafUpdate = requestAnimationFrame(() => {
    this._rafUpdate = null;
    this.update();
  });
}

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

    /* =========================================================
      CBH guard: prevent NaN / Infinity slider counters
      - If slides are stacked/wrapped (offsetLeft delta = 0), Dawn's maths divides by 0.
      - We gracefully fall back to a single-page counter and disable arrows.
    ========================================================= */
    if (!Number.isFinite(this.sliderItemOffset) || this.sliderItemOffset <= 0) {
      // Use a non-zero offset so other calculations never divide by 0
      this.sliderItemOffset = this.slider.clientWidth || 1;
      this.slidesPerPage = 1;
      this.totalPages = this.sliderItemsToShow.length || 1;
      this.currentPage = 1;

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      if (this.prevButton) this.prevButton.setAttribute('disabled', 'disabled');
      if (this.nextButton) this.nextButton.setAttribute('disabled', 'disabled');
      return;
    }

    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this._scheduleInitPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    // CBH: if SliderComponent bailed (missing markup), do not continue
    if (!this.slider) return;
    this.sliderItemsToShow = this.sliderItemsToShow || [];

    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    if (this.sliderControlButtons[this.currentPage - 1]) {
      this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
      this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', SlideshowComponent);
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.textContent = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}

class ProductRecommendations extends HTMLElement {
  observer = undefined;
  abortController = undefined;
  loadStarted = false;

  constructor() {
    super();
  }

  connectedCallback() {
    this.setAttribute('aria-busy', 'true');
    this.initializeRecommendations(this.dataset.productId);
  }

  disconnectedCallback() {
    this.observer?.disconnect?.();
    this.abortController?.abort?.();
  }

  get recommendationSurface() {
    return this.dataset.cbhRecommendationSurface || (this.hasAttribute('data-cbh-related-products') ? 'related' : 'generic');
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);

    const rootMargin = this.recommendationSurface === 'related' ? '0px 0px 1200px 0px' : '0px 0px 600px 0px';
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin }
    );
    this.observer.observe(this);
  }

  getRequestUrl(productId) {
    const url = new URL(this.dataset.url, window.location.href);
    if (productId && !url.searchParams.has('product_id')) url.searchParams.set('product_id', productId);
    if (this.dataset.sectionId && !url.searchParams.has('section_id')) {
      url.searchParams.set('section_id', this.dataset.sectionId);
    }
    return url.toString();
  }

  getResponseRecommendations(html) {
    const candidates = [...html.querySelectorAll('product-recommendations')];
    if (!candidates.length) return null;

    const sectionId = this.dataset.sectionId;
    const surface = this.recommendationSurface;
    return (
      candidates.find((candidate) => {
        const sameSection = !sectionId || candidate.dataset.sectionId === sectionId;
        const candidateSurface =
          candidate.dataset.cbhRecommendationSurface ||
          (candidate.hasAttribute('data-cbh-related-products') ? 'related' : 'generic');
        const sameSurface = surface === 'generic' || candidateSurface === surface;
        return sameSection && sameSurface;
      }) || candidates.find((candidate) => !sectionId || candidate.dataset.sectionId === sectionId) || candidates[0]
    );
  }

  hasRecommendationCards() {
    return !!this.querySelector('.product-card-wrapper, .grid__item, slideshow-component');
  }

  setStandaloneRelatedVisibility(hasCards) {
    if (this.recommendationSurface !== 'related' || !this.classList.contains('cbh-related-products-pro__recommendations')) {
      return;
    }

    const section = this.closest('.cbh-related-products-pro');
    if (!section) return;

    if (hasCards || document.documentElement.classList.contains('shopify-design-mode')) {
      section.hidden = false;
    } else {
      section.hidden = true;
    }
  }

  notifyRecommendationUpdate() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(
          new CustomEvent('cbh:product-recommendations:updated', { detail: { container: this } })
        );
      });
    });
  }

  loadRecommendations(productId) {
    if (this.loadStarted) return;
    this.loadStarted = true;
    this.abortController?.abort?.();
    this.abortController = new AbortController();

    fetch(this.getRequestUrl(productId), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Product recommendations request failed (${response.status})`);
        return response.text();
      })
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = this.getResponseRecommendations(html);

        if (recommendations) this.innerHTML = recommendations.innerHTML;

        const hasCards = this.hasRecommendationCards();
        this.setAttribute('aria-busy', 'false');
        this.dataset.cbhRecommendationState = hasCards ? 'loaded' : 'empty';
        this.classList.toggle('product-recommendations--loaded', hasCards);
        this.setStandaloneRelatedVisibility(hasCards);

        if (this.classList.contains('complementary-products') && !hasCards) {
          this.remove();
          return;
        }

        if (hasCards) this.notifyRecommendationUpdate();
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        this.setAttribute('aria-busy', 'false');
        this.dataset.cbhRecommendationState = 'error';
        this.setStandaloneRelatedVisibility(false);
        if (this.classList.contains('complementary-products')) this.remove();
        window.cbhLogError?.(error);
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

/* ------------------------------------------------------------
  CBH: Product card navigation guard
  - Prevent interactive widgets inside a card from triggering
    any underlying/nearby navigation links.
------------------------------------------------------------ */
document.addEventListener(
  'click',
  function (e) {
    const stopZone = e.target.closest(
      '.product-card-wrapper .mybadges,' +
        '.product-card-wrapper .mbadge,' +
        '.product-card-wrapper .lipscore-rating-small,' +
        '.product-card-wrapper [data-cbh-stop-card-nav],' +
        '.product-card-wrapper details,' +
        '.product-card-wrapper summary'
    );

    if (!stopZone) return;

    const nearestLink = e.target.closest('a');
    if (nearestLink) e.preventDefault();
    e.stopPropagation();
  },
  true
);
